import { Dispatch, SetStateAction, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '../core/Button';
import { DialogWindow } from '../Dialog';
import { PeriodDateRange } from '@/components/PeriodDateRange';
import type {
  CollectionTicketClaimDTO,
  OrganizationDTO,
} from '@/api/trafikportalen';

interface TenantDeletePeriodDialogProps {
  open: boolean;
  claims: CollectionTicketClaimDTO[];
  periodOrganizationCount: number;
  organizationsById: Map<string, OrganizationDTO>;
  onClose: () => void;
  onDelete: () => Promise<void>;
  isDeleting: boolean;
}

export const TenantDeletePeriodDialog = ({
  open,
  claims,
  periodOrganizationCount,
  organizationsById,
  onClose,
  onDelete,
  isDeleting,
}: TenantDeletePeriodDialogProps) => {
  const { formatMessage } = useIntl();

  const handleOpenChange: Dispatch<SetStateAction<boolean>> = useCallback(
    (openOrUpdater) => {
      const next =
        typeof openOrUpdater === 'function'
          ? openOrUpdater(open)
          : openOrUpdater;
      if (!next) onClose();
    },
    [onClose, open],
  );

  const affectedOrganizationCount = useMemo(
    () => new Set(claims.map((claim) => claim.organization_id)).size,
    [claims],
  );

  const totalOrganizationCount = Math.max(
    periodOrganizationCount,
    affectedOrganizationCount,
  );
  const affectsAllOrganizations =
    affectedOrganizationCount >= totalOrganizationCount;

  const scopeDescription = useMemo(() => {
    if (affectedOrganizationCount === 0) return '';
    if (affectsAllOrganizations) {
      return formatMessage(
        {
          defaultMessage:
            '{count, plural, one {Organisationen som använder den här perioden påverkas.} other {Alla organisationer som använder den här perioden påverkas ({count}).}}',
        },
        { count: totalOrganizationCount },
      );
    }
    return formatMessage(
      {
        defaultMessage:
          '{selected, plural, one {Bara den markerade organisationen påverkas (1 av {total}). Övriga organisationer som använder perioden ändras inte.} other {Bara de markerade organisationerna påverkas ({selected} av {total}). Övriga organisationer som använder perioden ändras inte.}}',
      },
      { selected: affectedOrganizationCount, total: totalOrganizationCount },
    );
  }, [
    affectedOrganizationCount,
    affectsAllOrganizations,
    formatMessage,
    totalOrganizationCount,
  ]);

  const handleDelete = useCallback(async () => {
    await onDelete();
  }, [onDelete]);

  return (
    <DialogWindow
      open={open}
      onOpenChange={handleOpenChange}
      title={formatMessage(
        {
          defaultMessage:
            '{count, plural, one {Radera period} other {Radera perioder}}',
        },
        { count: claims.length },
      )}
      description={scopeDescription}
      descriptionClassName={
        affectsAllOrganizations
          ? 'rounded-md bg-background-100 p-3 font-medium text-text-950'
          : 'rounded-md bg-yellow-500 p-3 font-medium text-contrast-yellow'
      }
      className="max-w-[560px]"
      content={
        <div className="flex flex-col gap-4">
          <p>
            <FormattedMessage
              defaultMessage="{count, plural, one {Är du säker på att du vill radera perioden? Den tas bort direkt.} other {Är du säker på att du vill radera # perioder? De tas bort direkt.}}"
              values={{ count: claims.length }}
            />
          </p>
          <ul
            aria-label={formatMessage({
              defaultMessage: 'Organisationer som påverkas',
            })}
            className="flex max-h-40 flex-col gap-1 overflow-y-auto text-text-800"
          >
            {claims.map((claim) => (
              <li key={claim.id}>
                {organizationsById.get(claim.organization_id)?.name ??
                  claim.organization_id}
                {': '}
                <PeriodDateRange from={claim.valid_from} to={claim.valid_to} />
              </li>
            ))}
          </ul>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="ghost" onClick={onClose}>
              <FormattedMessage defaultMessage="Avbryt" />
            </Button>
            <Button
              variant="filled"
              color="red"
              onClick={handleDelete}
              loading={isDeleting}
            >
              <FormattedMessage defaultMessage="Radera" />
            </Button>
          </div>
        </div>
      }
    />
  );
};
